import { apiCall } from '@/lib/api-client';

export interface BookingInput {
  patientId?: string;
  scheduledAt: string; // ISO 8601
  notes?: string;
}

/**
 * Patient summary embedded in BookingRow.
 *
 * Mirrors the API shape returned by GET /bookings/me and /bookings/due-soon
 * (see api/src/bookings/bookings.service.ts -> BookingWithPatient).
 *
 * lastNameDecrypted + mrnLast6 are the safe-to-display projections of the
 * server-side decrypted patient fields. They may be missing if the row
 * was attached to a patient whose CIN has no resolvable last_name (rare,
 * e.g. mid-migration). UI must render defensively.
 */
export interface BookingPatient {
  id: string;
  cinHash: string;
  lastNameDecrypted?: string;
  mrnLast6?: string;
}

export interface BookingRow {
  id: number;
  patientId: string;
  agentId?: string;
  scheduledAt: string; // ISO 8601
  status: 'scheduled' | 'completed' | 'missed' | 'rescheduled' | 'cancelled';
  notes?: string;
  createdAt: string;
  updatedAt: string;
  /** Embedded patient summary; may be undefined for rows where the patient row was deleted. */
  patient?: BookingPatient;
}

export interface MissedBookingRow {
  id: string;
  patientId: string;
  agentId: string;
  agentName: string;
  patientLastName: string;
  patientMrnLast6: string;
  scheduledAt: string;
  createdAt: string;
}

export interface BookingsResponse {
  data: BookingRow[];
}

export interface MissedBookingsResponse {
  data: MissedBookingRow[];
}

export interface PatientSearchResult {
  id: string;
  cinHash: string;
  cinLast6: string;
  firstName: string;
  lastName: string;
  dob: string;
  phone: string | null;
}

export const bookingsApi = {
  create: (input: BookingInput) =>
    apiCall<BookingRow>('/bookings', {
      method: 'POST',
      body: input,
    }),

  // E-38 · POST: la consulta (un teléfono o un CIN) va en el cuerpo, nunca en
  // la URL — en la URL quedaba en el log de acceso del servidor.
  searchPatients: (q: string, limit: number = 20) =>
    apiCall<PatientSearchResult[]>('/agents/me/clients/search', {
      method: 'POST',
      body: { q, limit },
    }),

  list: (filter: 'today' | 'week' | 'upcoming' | 'past' | 'all') =>
    apiCall<BookingRow[]>(`/bookings/me?filter=${filter}`),

  dueSoon: (minutes: number = 30) =>
    apiCall<BookingRow[]>(`/bookings/due-soon?minutes=${minutes}`),

  missed: (limit: number = 50, offset: number = 0) =>
    apiCall<MissedBookingsResponse>(`/bookings/missed?limit=${limit}&offset=${offset}`),

  update: (id: number, input: Partial<BookingInput> & { status?: string; agentId?: number }) =>
    apiCall<BookingRow>(`/bookings/${id}`, {
      method: 'PATCH',
      body: input,
    }),

  delete: (id: number) =>
    apiCall<void>(`/bookings/${id}`, {
      method: 'DELETE',
    }),

  /**
   * POST /bookings/:id/start-screening — find or create the assignment for
   * the agent + this booking's patient, returning { assignmentId }.
   *
   * 409 cases:
   *   - BOOKING_ALREADY_FINALIZED — booking is cancelled/completed/missed
   *   - PATIENT_ASSIGNED_TO_OTHER_AGENT — different agent currently owns
   */
  startScreening: (id: number) =>
    apiCall<{ assignmentId: string }>(`/bookings/${id}/start-screening`, {
      method: 'POST',
    }),

  /**
   * GET /bookings/check-availability — pre-validation, returns 200 with
   * { available: true } or { available: false, code, conflictingBookingId }.
   * Informational only — does not throw on conflict.
   */
  checkAvailability: (patientId: string, scheduledAtIso: string) =>
    apiCall<
      | { available: true }
      | {
          available: false;
          code: 'BOOKING_DUPLICATE' | 'AGENT_TIME_CONFLICT';
          conflictingBookingId: string;
        }
    >(
      `/bookings/check-availability?patientId=${encodeURIComponent(
        patientId,
      )}&scheduledAt=${encodeURIComponent(scheduledAtIso)}`,
    ),
};
